<template>
  <div class="as-website">

    <!-- Hero Section -->
    <v-container fluid class="hero-section py-20">
      <v-row align="center" justify="center">
        <v-col cols="12" lg="6" class="text-center text-lg-left">
          <!-- Badge -->
          <div class="mb-6">
            <v-chip
              color="var(--color-pallet-2)"
              text-color="white"
              class="font-weight-bold badge-chip"
              size="large"
            >
              {{ $t('pages.home-view.hero.badge') }}
            </v-chip>
          </div>

          <!-- One promise, in the visitor's words: less chasing, more training. Details live further
               down and on /features — the hero only has to make them want to look (Ansis, 2026-10-25). -->
          <h1 class="headline-text mb-4">
            {{ $t('pages.home-view.hero.headline') }}
            <span class="text-color-pallet-3">{{ $t('pages.home-view.hero.headline-highlight') }}</span>
          </h1>
          <p class="hero-subline mb-8">{{ $t('pages.home-view.hero.subline') }}</p>

          <div class="cta-section mb-6">
            <v-btn
              color="var(--color-pallet-3)"
              x-large
              elevation="4"
              rounded
              @click="onStartFree(location.HERO)"
            >
              {{ $t('pages.home-view.cta.start-free') }}
            </v-btn>
            <v-btn
              outlined
              color="var(--color-pallet-3)"
              x-large
              rounded
              target="_blank"
              :href="exampleStudioUrl"
              @click="onSeeExample(location.HERO)"
            >
              <v-icon left>mdi-eye</v-icon>
              {{ $t('pages.home-view.cta.see-example') }}
            </v-btn>
          </div>

          <!-- Reasons to try it now, not a feature list. -->
          <div class="trust-indicators">
            <v-chip small outlined>
              <v-icon left small>mdi-heart</v-icon>
              {{ $t('pages.home-view.hero.chips.made-in-latvia') }}
            </v-chip>
            <v-chip small outlined>{{ $t('pages.home-view.hero.chips.free-days', { days: freeTrialDays }) }}</v-chip>
            <v-chip small outlined>{{ $t('pages.home-view.hero.chips.no-card') }}</v-chip>
            <v-chip small outlined>{{ $t('pages.home-view.hero.chips.setup-help') }}</v-chip>
          </div>
        </v-col>

        <!-- Demo Image -->
        <v-col cols="12" lg="6" class="text-center">
          <div class="demo-container">
            <img 
              src="/img/devices_2.png" 
              :alt="$t('pages.home-view.hero.demo-image-alt')" 
              class="hero-img"
            />
          </div>
        </v-col>
      </v-row>
    </v-container>

    <home-pain-section />

    <home-how-it-works-section :example-url="exampleStudioUrl" @start="onStartFree(location.HOW_IT_WORKS)" @see-example="onSeeExample(location.HOW_IT_WORKS)" />

    <!-- Testimonials (restored as they were, Ansis 2026-10-25), moved up (website-content-spec §13.2):
         visitors come to check that ASport is real and serious. No founder name (Ansis, 2026-10-26). -->
    <v-container fluid class="py-14 as-home-section--white">
      <v-row justify="center">
        <v-col cols="12" md="8" class="text-center">
          <h2 class="text-color-pallet-2 text-h3 font-weight-bold mb-4">{{ $t('pages.home-view.testimonials.title') }}</h2>
          
          <v-row>
            <v-col cols="12" md="6" class="mb-6">
              <div class="testimonial-card clickable-testimonial">
                <div class="testimonial-content">
                  <p class="text-body-1 text-color-pallet-2 mb-2 italic">"{{ $t('pages.home-view.testimonials.testimonial1') }}"</p>
                  <div class="testimonial-author">
                    <div class="author-info">
                      <v-avatar 
                        size="40" 
                        class="client-logo"
                      >
                        <v-icon size="24" color="var(--color-pallet-1)">mdi-meditation</v-icon>
                      </v-avatar>
                      <div class="author-details">
                        <h4 class="text-body-2 font-weight-medium text-color-pallet-2 mb-0">
                          {{ $t('pages.home-view.testimonials.testimonial1-author') }}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6" class="mb-6">
              <router-link 
                :to="exampleStudioUrl"
                class="testimonial-link"
                target="_blank"
              >
                <div class="testimonial-card clickable-testimonial">
                  <div class="testimonial-content">
                    <p class="text-body-1 text-color-pallet-2 mb-2 italic">"{{ $t('pages.home-view.testimonials.testimonial2') }}"</p>
                    <div class="testimonial-author">
                      <div class="author-info">
                        <v-avatar 
                          size="40" 
                          class="client-logo"
                        >
                          <img :src="exampleStudioLogoUrl" alt="Vingro ar Signi" />
                        </v-avatar>
                        <div class="author-details">
                          <h4 class="text-body-2 font-weight-medium text-color-pallet-2 mb-0">
                            {{ $t('pages.home-view.testimonials.testimonial2-author') }}
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </router-link>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>

    <home-features-section />

    <!-- Pricing section -->
    <v-container id="pricing" fluid class="py-14 as-home-section--white">
      <v-row justify="center">
        <v-col cols="12" md="10" class="text-center">
          <h2 class="text-color-pallet-2 text-h3 font-weight-bold mb-4">{{ $t('pages.home-view.pricing.title') }}</h2>
          
          <!-- Intro text -->
          <div class="pricing-intro mb-2">
            <p class="text-body-1 text-color-grey">
              {{ $t('pages.home-view.pricing.intro') }}
              <br>
              <i>{{ $t('pages.home-view.pricing.intro-note') }}</i>
            </p>
          </div>
          
          <!-- Individual trainer plans subtitle -->
          <div class="individual-trainer-badge mb-6 pb-4">
            <v-chip 
              color="var(--color-pallet-1)" 
              text-color="white" 
              small
              class="individual-trainer-chip mt-2"
            >
            <v-icon left small>mdi-account</v-icon>
              {{ $t('pages.home-view.pricing.individual-trainers') }}
            </v-chip>
            <!-- Promotional banner moved below -->
            <v-chip 
              color="var(--color-pallet-4)" 
              text-color="white"
              small
              class="individual-trainer-chip ml-2 mt-2"
            >
              <v-icon left small>mdi-gift</v-icon>
              {{ $t('pages.home-view.pricing.free-month') }}
            </v-chip>
            <v-chip 
              outlined 
              color="var(--color-pallet-1)" 
              small
              class="individual-trainer-chip ml-2 mt-2"
            >
              <v-icon left small>mdi-shield-check</v-icon>
              {{ $t('pages.home-view.pricing.cancel-anytime') }}
            </v-chip>
          </div>
          
          <!-- Pricing Plans Grid -->
          <v-row>
            <!-- Individual Trainer Plans -->
            <v-col 
              v-for="(plan, index) in individualTrainerPlans" 
              :key="index"
              cols="12" 
              sm="6" 
              lg="3" 
              class="mb-4"
            >
              <PricingCard 
                :plan="plan" 
                :link="getLocalisedUrl('/signup?plan=' + plan.id)"
              />
            </v-col>
            
            <!-- Organization -->
            <v-col cols="12" sm="6" lg="3" class="mb-4">
              <PricingCard 
                :plan="organizationPlan" 
                :link="getLocalisedUrl('/pricing/organization')"
                card-class="organization-card-variant"
              />
            </v-col>
          </v-row>
          
          <!-- Both price lists: the studio plans exist today, so they get their own link too. -->
          <div class="pricing-read-more mt-6 mb-8">
            <router-link :to="getLocalisedUrl('/' + mainPath.PRICING)" class="pricing-details-link">
              {{ $t('pages.home-view.pricing.compare-plans') }}
            </router-link>
            <router-link :to="getLocalisedUrl('/' + mainPath.PRICING_ORGANIZATION)" class="pricing-details-link">
              {{ $t('pages.home-view.pricing.compare-studio-plans') }}
            </router-link>
          </div>
          
        </v-col>
      </v-row>
    </v-container>

    <!-- FAQ Section -->
    <v-container id="faq" fluid class="py-14 as-home-section--grey">
      <v-row justify="center">
        <v-col cols="12" md="8" class="text-center">
          <h2 class="text-color-pallet-2 text-h3 font-weight-bold mb-8">{{ $t('pages.home-view.faq.title') }}</h2>
          
          <!-- The few questions that decide a first visit; the rest are on /faq. -->
          <faq-list :items="homeFaqItems" :location="location.FAQ" />
          <router-link :to="faqUrl" class="as-home-text-link" @click.native="onAllQuestions">
            {{ $t('pages.home-view.faq.all-link') }} →
          </router-link>
        </v-col>
      </v-row>
    </v-container>

    <home-closing-section :studio-contact-url="studioContactUrl" @start="onStartFree(location.CLOSING)" @talk="onTalk(location.CLOSING)" />
  </div>
</template>

<script>
import urlUtil from '@/js/utils/urlUtil'
import { getIndividualTrainerPlans } from '@/data/pricingPlans.js'
import PricingCard from '@/components/PricingCard.vue'
import HomePainSection from '@/domains/website/components/home/HomePainSection.vue'
import HomeHowItWorksSection from '@/domains/website/components/home/HomeHowItWorksSection.vue'
import HomeFeaturesSection from '@/domains/website/components/home/HomeFeaturesSection.vue'
import HomeClosingSection from '@/domains/website/components/home/HomeClosingSection.vue'
import { EXAMPLE_STUDIO_IDENTIFIER, FREE_TRIAL_DAYS, studioContactHref } from '@/domains/website/websiteLinks'
import { trackWebsiteEvent, WebsiteCta, WebsiteEvent, WebsiteLocation } from '@/domains/website/websiteTracking'
import { HOME_FAQ_ITEMS } from '@/domains/website/faqCatalogue'
import FaqList from '@/domains/website/components/FaqList.vue'
import { MainPath } from '@/router/mainPaths'

/** `/?section=faq` — the old way to reach the FAQ on the home page. */
const LEGACY_SECTION_QUERY = 'section'
const LEGACY_FAQ_SECTION = 'faq'


export default {
  name: 'HomeView',
  components: {
    PricingCard,
    HomePainSection,
    HomeHowItWorksSection,
    HomeFeaturesSection,
    HomeClosingSection,
    FaqList
  },
  data: () => ({
    /** Re-exposed for the template (Vue 2 templates see only data/computed/methods). */
    location: WebsiteLocation,
    freeTrialDays: FREE_TRIAL_DAYS,
    mainPath: MainPath,
  }),
  metaInfo () {
    return {
      title: this.$t('pages.home-view.seo-title') || '' ,
      meta: [
        { name: 'title', content: this.$t('pages.home-view.seo-title') || '' },
        { name: 'description', content: this.$t('pages.home-view.seo-description') || '' },
        { name: 'url', content: window.location.href || '' },
        { property: 'og:title', content: this.$t('pages.home-view.seo-title') || '' },
        { property: 'og:description', content: this.$t('pages.home-view.seo-description') || '' },
      ]
    }
  },
  computed: {
    landingUrl: function() {
      return this.$store.getters['session/landingUrl']
    },
    referrerUrl: function() {
      return this.$store.getters['session/referrerUrl']
    },
    individualTrainerPlans: function() {
      return getIndividualTrainerPlans()
    },
    organizationPlan: function() {
      return {
        title: this.$t('pages.home-view.pricing.organization-plan.title'),
        icon: 'mdi-account-group',
        price: this.$t('pages.home-view.pricing.organization-plan.price'),
        period: this.$t('pages.home-view.pricing.organization-plan.period'),
        ctaText: this.$t('pages.home-view.cta.talk'),
        // A studio talks first (trainers are added by ASport) — the card opens an e-mail, R4.
        ctaHref: this.studioContactUrl,
        // …and links to the studio plans, so they read as available today, not as a custom offer.
        detailsUrl: this.getLocalisedUrl('/' + MainPath.PRICING_ORGANIZATION),
        detailsLabel: this.$t('pages.home-view.pricing.see-studio-plans'),
        description: this.$t('pages.home-view.pricing.organization-plan.description'),
        isFeatured: false
      }
    },
    homeFaqItems: function() {
      return HOME_FAQ_ITEMS
    },
    faqUrl: function() {
      return this.getLocalisedUrl('/' + MainPath.FAQ)
    },
    exampleStudioUrl: function() {
      return this.getLocalisedOrganizationUrl({ identifier: EXAMPLE_STUDIO_IDENTIFIER })
    },
    exampleStudioLogoUrl: function() {
      return `/img/organization/${EXAMPLE_STUDIO_IDENTIFIER}/logo_144.jpg`
    },
    studioContactUrl: function() {
      return studioContactHref(this.$t('pages.home-view.cta.talk-email-subject'))
    }
  },
  created: async function() {
    this.$mixpanel.track('ViewHomePage', {
        landingUrl: this.landingUrl,
        referrerUrl: this.referrerUrl
    });
    
    // Old links (`/?section=faq`, shared before the FAQ got its own page) open /faq.
    if (this.$route.query[LEGACY_SECTION_QUERY] === LEGACY_FAQ_SECTION) {
      this.$router.replace(this.faqUrl)
    }
  },
  methods: {
    ...urlUtil,
    goToSignup() {
      // If user is already logged in, redirect to mypages instead of signup
      if (this.$store.getters['user/isLoggedIn']) {
        this.$router.push(this.getLocalisedMyPagesUrl())
      } else {
        // Navigate to signup page
        this.$router.push(this.getLocalisedUrl('/signup'))
      }
    },
    onStartFree(location) {
      trackWebsiteEvent(this.$gtag, WebsiteEvent.CTA_CLICK, { cta: WebsiteCta.START_FREE, location })
      this.goToSignup()
    },
    onSeeExample(location) {
      trackWebsiteEvent(this.$gtag, WebsiteEvent.CTA_CLICK, { cta: WebsiteCta.SEE_EXAMPLE, location })
    },
    onTalk(location) {
      trackWebsiteEvent(this.$gtag, WebsiteEvent.CTA_CLICK, { cta: WebsiteCta.TALK, location })
    },
    onAllQuestions() {
      trackWebsiteEvent(this.$gtag, WebsiteEvent.CTA_CLICK, { cta: WebsiteCta.ALL_QUESTIONS, location: WebsiteLocation.FAQ })
    }
  }
}
</script>

<style scoped>
/* === CORE LAYOUT === */
.as-website {
  background: var(--color-background);
  min-height: 100vh;
}

/* === HERO SECTION === */
.hero-section {
  background: linear-gradient(135deg, var(--color-pallet-1) 0%, var(--color-pallet-2) 100%);
  color: white;
  position: relative;
  overflow: hidden;
  min-height: 80vh;
  display: flex;
  align-items: center;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="40" r="1.5" fill="rgba(255,255,255,0.1)"/><circle cx="40" cy="80" r="1" fill="rgba(255,255,255,0.1)"/></svg>');
  opacity: 0.3;
  /* Decoration only: it lies over the hero, so let clicks and text selection go through to the content. */
  pointer-events: none;
}

.headline-text {
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.text-color-grey {
  color: rgba(255, 255, 255, 0.95) !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.text-color-pallet-3 {
  color: var(--color-pallet-3) !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.demo-container {
  position: relative;
  max-width: 500px;
  margin: 0 auto;
}

.hero-img {
  max-width: 100%;
  height: auto;
  border-radius: 12px;
  transition: transform 0.3s ease;
  background: transparent !important;
  box-shadow: none !important;
}

.hero-img:hover {
  transform: translateY(-5px);
}

.cta-section {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
}

/* Sub-line under the hero headline. */
.hero-subline {
  font-size: 1.2rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.95);
  max-width: 620px;
  margin-left: auto;
  margin-right: auto;
}

/* From lg the hero text is left-aligned (text-lg-left); sub-line, buttons and chips follow it. */
@media (min-width: 1264px) {
  .hero-subline {
    margin-left: 0;
  }

  .hero-section .cta-section,
  .hero-section .trust-indicators {
    justify-content: flex-start;
  }
}

.trust-indicators {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.trust-indicators .v-chip {
  color: white !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.badge-chip {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  font-weight: 700 !important;
}

/* === CONTENT SECTIONS === */

/* === TESTIMONIALS === */
.testimonial-card {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  height: 100%;
  border: 1px solid rgba(46, 139, 87, 0.1);
  position: relative;
}

.testimonial-card::before {
  content: '"';
  position: absolute;
  top: -10px;
  left: 20px;
  font-size: 4rem;
  color: var(--color-pallet-1);
  opacity: 0.2;
  font-family: serif;
}

.testimonial-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(46, 139, 87, 0.12);
}

.testimonial-content {
  position: relative;
  z-index: 1;
}

.testimonial-author {
  display: flex;
  align-items: center;
  margin-top: 1rem;
}

.testimonial-link {
  text-decoration: none;
  display: block;
  transition: transform 0.3s ease;
}

.clickable-testimonial {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.clickable-testimonial:hover {
  border-color: var(--color-pallet-1);
  box-shadow: 0 8px 30px rgba(46, 139, 87, 0.15);
}

.author-info {
  display: flex;
  align-items: center;
  margin-top: 1rem;
}

.author-details {
  display: flex;
  flex-direction: column;
  margin-left: 1rem;
}

.client-logo {
  border: 2px solid var(--color-pallet-1);
  box-shadow: 0 2px 8px rgba(46, 139, 87, 0.2);
  transition: all 0.3s ease;
}

.clickable-testimonial:hover .client-logo {
  box-shadow: 0 4px 12px rgba(46, 139, 87, 0.3);
}

.client-logo img {
  border-radius: 50%;
  object-fit: cover;
}

/* === PRICING === */
.individual-trainer-badge {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
}

.individual-trainer-chip {
  margin: 0.25rem;
}

.pricing-read-more {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 32px;
  text-align: left;
  border-bottom: 1px solid rgba(46, 139, 87, 0.1);
  padding-bottom: 2rem;
}

.pricing-details-link {
  color: var(--color-pallet-1) !important;
  text-decoration: underline;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-block;
  line-height: 1.4;
  word-wrap: break-word;
  max-width: 100%;
}

.pricing-details-link:hover {
  color: var(--color-pallet-2) !important;
  text-decoration: underline;
}

/* === COLOR CLASSES === */
.text-color-pallet-1 {
  color: var(--color-pallet-1) !important;
}

.text-color-pallet-2 {
  color: var(--color-pallet-2) !important;
}

.text-color-grey {
  color: var(--color-grey) !important;
}

/* === RESPONSIVE === */
@media (max-width: 960px) {
  .headline-text {
    font-size: 2.5rem;
  }
  
  .hero-img {
    max-width: 700px;
  }
  
  .hero-section {
    min-height: 70vh;
  }
}

@media (max-width: 600px) {
  .headline-text {
    font-size: 2rem !important;
    line-height: 1.2 !important;
    margin-bottom: 1rem !important;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }
  
  .cta-section {
    flex-direction: column;
    align-items: center;
    margin-bottom: 1rem !important;
  }
  
  .cta-section .v-btn {
    width: 100%;
    max-width: 320px;
    margin-bottom: 0.75rem !important;
    margin-right: 0 !important;
    font-size: 0.95rem !important;
    padding: 0.875rem 1.5rem !important;
    height: auto !important;
    min-height: 48px !important;
  }

  .cta-section .v-btn__content {
    white-space: normal;
  }
  
  .hero-img {
    max-width: 100%;
    margin-top: 1rem;
  }
  
  .hero-section {
    min-height: auto !important;
    height: auto !important;
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
    display: block !important;
  }
  
  .hero-section .v-container {
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
    max-width: 100% !important;
  }
  
  .hero-section .v-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  
  .hero-section .v-col {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
  
  .trust-indicators {
    justify-content: center;
    gap: 0.2rem;
    flex-wrap: wrap;
  }
  
  .trust-indicators .v-chip {
    font-size: 0.8rem !important;
    height: auto !important;
    padding: 0.3rem 0.6rem !important;
    margin: 0.15rem !important;
  }
  
  .badge-chip {
    font-size: 0.9rem !important;
    padding: 0.4rem 0.8rem !important;
  }
}

@media (max-width: 400px) {
  .headline-text {
    font-size: 1.8rem !important;
    line-height: 1.2 !important;
  }
  
  .hero-section .v-container {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
  
  .hero-section .v-col {
    padding-left: 0.25rem !important;
    padding-right: 0.25rem !important;
  }
  
  .cta-section .v-btn {
    font-size: 0.9rem !important;
    padding: 0.8rem 1.2rem !important;
    max-width: 300px !important;
  }
  
}
</style>